import React from 'react'
import { ReactComponent as CheckmarkSmallIcon } from '@assets/images/Icons/Checkmark_small.svg'
import Tooltip from '../Tooltip/Tooltip.component'
import type { MetricCardProps } from './MetricCard.types'
import {
  METRIC_CARD_DEMO_DELTA,
  METRIC_CARD_DEMO_FOOTER,
  METRIC_CARD_DEMO_LABEL,
  METRIC_CARD_DEMO_PREVIOUS_FOOTER,
  METRIC_CARD_DEMO_UNIT,
  METRIC_CARD_DEMO_VALUE,
} from './MetricCard.types'
import { formatMetricCardDelta } from './MetricCard.utils'
import './MetricCard.css'

const MetricCard: React.FC<MetricCardProps> = ({
  label,
  labelIcon,
  labelTone = 'default',
  rightLabel,
  value,
  valueOver,
  unitInline,
  delta,
  deltaTone = 'increase',
  deltaPlacement = 'value',
  deltaShowArrow = false,
  statusBadge,
  valueTone = 'default',
  footer,
  showFooter = true,
  trend,
  infoTooltip,
  showInfoIcon = false,
  showWarningIcon = false,
  size = 'md',
  variant = 'card',
  className = '',
  darkMode = false,
  previewState,
}) => {
  const isPreview = previewState != null

  const isHeaderBadges = previewState === 'header-badges'

  const resolvedVariant = previewState === 'plain' ? 'plain' : variant

  const resolvedSize =
    previewState === 'size-lg' || previewState === 'with-delta' ? 'lg' : size

  const resolvedLabel =
    label ??
    (isHeaderBadges
      ? 'Scheduled today'
      : isPreview
        ? METRIC_CARD_DEMO_LABEL
        : undefined)

  const resolvedLabelTone = isHeaderBadges ? 'primary' : labelTone

  const resolvedValue =
    value ??
    (isHeaderBadges
      ? '22'
      : previewState === 'size-lg' || previewState === 'with-delta'
        ? '1,248'
        : isPreview
          ? METRIC_CARD_DEMO_VALUE
          : undefined)

  const resolvedUnitInline =
    unitInline ??
    (previewState === 'with-unit' ? METRIC_CARD_DEMO_UNIT : undefined)

  const resolvedDelta =
    delta ??
    (isHeaderBadges
      ? 10
      : previewState === 'with-delta' || previewState === 'size-lg'
        ? METRIC_CARD_DEMO_DELTA
        : undefined)

  const resolvedDeltaTone =
    previewState === 'with-delta' ||
    previewState === 'size-lg' ||
    isHeaderBadges
      ? 'increase'
      : deltaTone

  const resolvedDeltaPlacement = isHeaderBadges ? 'header' : deltaPlacement

  const resolvedDeltaShowArrow = isHeaderBadges ? true : deltaShowArrow

  const resolvedStatusBadge = isHeaderBadges ? 'success' : statusBadge

  const resolvedValueTone =
    previewState === 'value-danger' ? 'danger' : valueTone

  const resolvedShowInfo =
    showInfoIcon ||
    previewState === 'with-info' ||
    previewState === 'with-delta' ||
    previewState === 'size-lg' ||
    isHeaderBadges

  const resolvedShowWarning = showWarningIcon || previewState === 'with-warning'

  const resolvedShowFooter =
    showFooter && previewState !== 'no-footer' && !isHeaderBadges

  const resolvedTrend =
    previewState === 'negative-trend'
      ? 'negative'
      : previewState === 'positive-trend' || previewState === 'default'
        ? 'positive'
        : previewState === 'plain'
          ? 'positive'
          : previewState === 'value-danger'
            ? 'muted'
            : previewState === 'with-delta' || previewState === 'size-lg'
              ? 'muted'
              : (trend ?? (footer != null ? 'muted' : undefined))

  const footerContent = (() => {
    if (!resolvedShowFooter) {
      return undefined
    }

    if (footer != null && footer !== '') {
      return footer
    }

    if (previewState === 'with-delta' || previewState === 'size-lg') {
      return METRIC_CARD_DEMO_PREVIOUS_FOOTER
    }

    if (
      previewState === 'default' ||
      previewState === 'positive-trend' ||
      previewState === 'negative-trend' ||
      previewState === 'value-danger' ||
      previewState === 'plain'
    ) {
      return METRIC_CARD_DEMO_FOOTER
    }

    return undefined
  })()

  const infoTooltipText =
    infoTooltip ??
    (resolvedLabel ? `More info about ${resolvedLabel}` : 'More info')

  const deltaLabel =
    resolvedDelta != null
      ? resolvedDeltaShowArrow
        ? formatMetricCardDelta(resolvedDelta, resolvedDeltaTone).replace(
            /^[+-]/,
            '',
          )
        : formatMetricCardDelta(resolvedDelta, resolvedDeltaTone)
      : undefined

  const classNames = [
    'ds-metric-card',
    `ds-metric-card--${resolvedVariant}`,
    `ds-metric-card--size-${resolvedSize}`,
    resolvedLabelTone === 'primary' ? 'ds-metric-card--label-primary' : '',
    resolvedValueTone === 'danger' ? 'ds-metric-card--value-danger' : '',
    footerContent != null && resolvedTrend
      ? `ds-metric-card--trend-${resolvedTrend}`
      : '',
    darkMode ? 'ds-metric-card--dark' : '',
    isPreview ? 'ds-metric-card--preview' : '',
    previewState ? `ds-metric-card--preview-${previewState}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const showTrendIcon =
    footerContent != null &&
    (resolvedTrend === 'positive' || resolvedTrend === 'negative')

  const showValueDelta =
    deltaLabel != null && resolvedDeltaPlacement === 'value'
  const showHeaderDelta =
    deltaLabel != null && resolvedDeltaPlacement === 'header'
  const showHeaderBadges = showHeaderDelta || resolvedStatusBadge != null

  const deltaArrowClass =
    resolvedDeltaTone === 'decrease'
      ? 'mgc_arrow_right_down_line'
      : 'mgc_arrow_right_up_line'

  const renderDeltaBadge = (placement: 'value' | 'header'): React.ReactNode => (
    <span
      className={`ds-metric-card__delta ds-metric-card__delta--${resolvedDeltaTone} ds-metric-card__delta--${placement}`}
    >
      {resolvedDeltaShowArrow ? (
        <i
          className={`mgc ${deltaArrowClass} ds-metric-card__delta-arrow`}
          aria-hidden="true"
        />
      ) : null}
      {deltaLabel}
    </span>
  )

  const valueRowClass = [
    'ds-metric-card__value',
    resolvedUnitInline || showValueDelta ? 'ds-metric-card__value--inline' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <article className={classNames}>
      <div className="ds-metric-card__header">
        <span className="ds-metric-card__label">
          {labelIcon ?? resolvedLabel}
          {resolvedShowInfo && (labelIcon ?? resolvedLabel) ? (
            <Tooltip content={infoTooltipText} placement="top">
              <span
                className="ds-metric-card__info"
                role="img"
                aria-label={infoTooltipText}
              >
                <i className="mgc mgc_information_fill" aria-hidden="true" />
              </span>
            </Tooltip>
          ) : null}
        </span>
        {rightLabel || resolvedShowWarning ? (
          <span className="ds-metric-card__right">
            {rightLabel}
            {resolvedShowWarning ? (
              <i
                className="mgc mgc_warning_fill ds-metric-card__warning"
                aria-hidden="true"
              />
            ) : null}
          </span>
        ) : null}
        {showHeaderBadges ? (
          <span className="ds-metric-card__badges">
            {resolvedStatusBadge === 'success' ? (
              <span
                className="ds-metric-card__status-badge ds-metric-card__status-badge--success"
                role="img"
                aria-label="On track"
              >
                <CheckmarkSmallIcon
                  className="ds-metric-card__status-check"
                  aria-hidden="true"
                />
              </span>
            ) : null}
            {showHeaderDelta ? renderDeltaBadge('header') : null}
          </span>
        ) : null}
      </div>

      <p className={valueRowClass}>
        <span className="ds-metric-card__value-text">{resolvedValue}</span>
        {resolvedUnitInline ? (
          <strong className="ds-metric-card__unit">{resolvedUnitInline}</strong>
        ) : null}
        {valueOver != null ? (
          <>
            {' / '}
            <span className="ds-metric-card__value-over">{valueOver}</span>
          </>
        ) : null}
        {showValueDelta ? renderDeltaBadge('value') : null}
      </p>

      {footerContent != null ? (
        <div className="ds-metric-card__footer">
          {showTrendIcon ? (
            <i
              className={`mgc ${
                resolvedTrend === 'negative'
                  ? 'mgc_arrow_down_circle_fill'
                  : 'mgc_arrow_up_circle_fill'
              }`}
              aria-hidden="true"
            />
          ) : null}
          {footerContent}
        </div>
      ) : null}
    </article>
  )
}

export default MetricCard
